import { defineStore } from 'pinia';
import { computed, ref, shallowRef, triggerRef, onUnmounted, getCurrentInstance, toRaw } from 'vue';
import { ApiDuty, DutyCalendar, ApiDutiesResponse, ApiDutySummary, Duty, DutyBreak } from '@/rennovation/planning-view/types/api/duties-response';
import { useDutyApi } from '@/rennovation/common/services/api/useDutyApi';
import { useExchangeApi } from '@/rennovation/common/services/api/useExchangeApi';
import { useAgreementApi } from '@/rennovation/common/services/api/useAgreementApi';
import { DateTime } from 'luxon';
import { useDayStore } from './useDayStore';
import { usePlanningStore } from './usePlanningStore';
import { DeleteDutyPayload, UpdateDutyPayload } from '../types/payloads/duty-payloads';
import { DutyAvailabilityResponse, DutyActivity, CurrentDayDuty } from '@/rennovation/planning-view/types/api/duties-response';
import { AgreementsApiResponse } from '@/rennovation/planning-view/types/api/agreement-api-response';
import { ApiCreateDutyRequest } from '@/rennovation/planning-view/types/api/duties-request';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { usePlanningBridgeStore } from './bridge/planningBridge';
import i18n from '@/plugins/i18n';

interface currentDayDuty {
	_id: string;
	displayName: string;
}

const t = (key: string) => i18n.global.t(key);

export const useDutyStore = defineStore('duty', () => {
	const dutiesById = shallowRef<Record<string, ApiDuty>>({});
	const duties = shallowRef<ApiDuty[]>([]);
	const deletedDuties = shallowRef<Record<string, ApiDuty>>({});
	const dutiesMappedById = shallowRef<Record<string, ApiDuty>>({});
	const dutiesMappedByDay = shallowRef<DutyCalendar>({});
	const mappedByDayVersion = ref(0);
	const unallocatedDutiesMappedByDay = shallowRef<Record<string, ApiDuty>>({});
	const unallocatedDutiesById = shallowRef<Record<string, ApiDuty>>({});
	const agreementsByDuty = shallowRef<Record<string, AgreementsApiResponse[]>>({});

	const currentDayDuties = ref<DutyAvailabilityResponse>(null);
	const currentDayDutiesById = ref<Record<string, CurrentDayDuty>>({});
	const agreements = shallowRef<AgreementsApiResponse[]>([]);
	const fetchError = ref<string | null>(null);
	const dutiesWithSameRootID = shallowRef<any[]>([]);
	const dayStore = useDayStore();
	const alertStore = useAlertStore();

	const updateStatistics = ref(0);

	// ============================================
	// BACKGROUND WORKER STATE
	// ============================================
	const isBackgroundLoading = ref(false);
	const loadingProgress = ref(0);
	let fetchWorker: Worker | null = null;

	// Safely attach lifecycle hook only if instantiated within a component
	if (getCurrentInstance()) {
		onUnmounted(() => {
			terminateWorker();
		});
	}

	// ============================================
	// GETTERS
	// ============================================
	const allDuties = computed((): ApiDuty[] => Object.values(dutiesById.value));

	// ============================================
	// ACTIONS
	// ============================================
	const dutyApi = useDutyApi();
	const exchangeApi = useExchangeApi();
	const agreementApi = useAgreementApi();
	const planningStore = usePlanningStore();

	/**
	 * Terminate running background worker thread safely
	 */
	function terminateWorker() {
		if (fetchWorker) {
			fetchWorker.terminate();
			fetchWorker = null;
		}
		isBackgroundLoading.value = false;
	}

	/**
	 * Background streaming helper for loading users in non-blocking worker chunks
	 */
	function startBackgroundDutiesFetch({
		from,
		to,
		statsDate,
		users,
		chunkSize = 20,
	}: {
		from: DateTime | string;
		to: DateTime | string;
		statsDate: DateTime | string;
		users: string[];
		chunkSize?: number;
	}) {
		if (!users || users.length === 0) return;

		// Cleanup existing worker task if running
		terminateWorker();

		isBackgroundLoading.value = true;
		loadingProgress.value = 0;

		const fromIso = typeof from === 'string' ? from : from && typeof (from as any).toISODate === 'function' ? (from as any).toISODate() : String(from);

		const toIso = typeof to === 'string' ? to : to && typeof (to as any).toISODate === 'function' ? (to as any).toISODate() : String(to);
		const statsDateIso =
			typeof statsDate === 'string'
				? statsDate
				: statsDate && typeof (statsDate as any).toISODate === 'function'
					? (statsDate as any).toISODate()
					: String(statsDate);
		const rawUsers = Array.from(toRaw(users)).map((user) => String(user));

		const token = usePlanningBridgeStore().token;
		const apiEndpoint = import.meta.env.VITE_API_BASE_URL || '/api';
		fetchWorker = new Worker(new URL('../workers/dutyFetcher.worker.ts', import.meta.url), { type: 'module' });
		fetchWorker.onmessage = (event: MessageEvent) => {
			const { type, payload } = event.data;

			if (type === 'CHUNK_LOADED') {
				const { duties: chunkDuties, chunkIndex, totalChunks, stats } = payload;

				const updatedDaysMap = { ...dutiesMappedByDay.value };
				for (const [userId, userDays] of Object.entries(chunkDuties as DutyCalendar)) {
					updatedDaysMap[userId] = {
						...updatedDaysMap[userId],
						...userDays,
					};
				}
				dutiesMappedByDay.value = updatedDaysMap;
				notifyMappedByDayChanged();
				const updatedIdMap = { ...dutiesMappedById.value };
				for (const daysMap of Object.values(chunkDuties as DutyCalendar)) {
					for (const duty of Object.values(daysMap)) {
						if (duty && duty.id) {
							updatedIdMap[duty.id] = duty;
						}
					}
				}
				dutiesMappedById.value = updatedIdMap;

				triggerRef(dutiesMappedByDay);
				triggerRef(dutiesMappedById);
				planningStore.setStats(stats);

				loadingProgress.value = Math.round(((chunkIndex + 1) / totalChunks) * 100);
			}

			if (type === 'ALL_CHUNKS_LOADED') {
				isBackgroundLoading.value = false;
				terminateWorker();
			}

			if (type === 'RESTORE_DUTY_LOADED') {
				const { duties } = payload;
				const dutyMap = duties?.data ?? duties ?? {};

				const currentDuties = deletedDuties.value || {};

				if (Array.isArray(dutyMap)) {
					dutyMap.forEach((duty) => {
						if (duty?.id) {
							currentDuties[duty.id] = duty;
						}
					});
				} else if (typeof dutyMap === 'object' && dutyMap !== null) {
					Object.assign(currentDuties, dutyMap);
				}

				deletedDuties.value = currentDuties;

				triggerRef(deletedDuties);
			}

			if (type === 'CHUNK_ERROR') {
				console.error(`Worker error on chunk ${payload.chunkIndex}:`, payload.error);
			}
		};

		fetchWorker.postMessage({
			type: 'START_FETCH_DUTIES',
			payload: {
				from: String(fromIso),
				to: String(toIso),
				statsDate: String(statsDateIso),
				driverIds: rawUsers,
				chunkSize: Number(chunkSize),
				apiEndpoint: String(apiEndpoint),
				token: String(token),
			},
		});
	}

	async function createDuty(payload: ApiCreateDutyRequest): Promise<ApiDuty> {
		try {
			fetchError.value = null;
			const result = await dutyApi.createDuty(payload);
			alertStore.showAlert(t('duty_create_success'), 'success');
			return result;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function updateDuty(dutyID: string, payload: UpdateDutyPayload | ApiCreateDutyRequest): Promise<any> {
		try {
			const res = await dutyApi.updateDuty(dutyID, payload);

			alertStore.showAlert(t('duty_update_success'), 'success');
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function fetchDutyByID(dutyId: string): Promise<ApiDuty> {
		try {
			fetchError.value = null;
			const duty = await dutyApi.findDutyByID(dutyId);

			dutiesById.value[dutyId] = duty;
			triggerRef(dutiesById);

			return duty;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function createNewDuty({ duty }: { duty: any }) {
		try {
			if (!duty.name) {
				switch (duty.type) {
					case 'compensation':
						duty.name = 'A';
						break;
					case 'rest':
						duty.name = 'R';
						break;
					case 'vacation':
						duty.name = 'F';
						break;
					case 'reserve':
						duty.name = 'SD';
						break;
					default:
						duty.name = 'D';
						break;
				}
			}

			if (!duty.user) {
				duty.user = null;
			}

			const result = await dutyApi.createDuty(duty);
			alertStore.showAlert(t('duty_create_success'), 'success');
			return result;
		} catch (err) {
			console.error('Create New Duty Error:', err);
			const errorMessage = err?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
		}
	}

	async function fetchAvailableDutiesByDay(day: string): Promise<DutyAvailabilityResponse> {
		try {
			fetchError.value = null;
			const response = await dutyApi.fetchAvailableDutiesByDay(day);

			currentDayDuties.value = response || [];
			const data = response?.data?.all || [];

			const updatedIdMap = data.reduce((map: Record<string, any>, duty: any) => {
				if (duty?._id) {
					map[duty._id] = duty;
				}
				return map;
			}, {});

			currentDayDutiesById.value = updatedIdMap;
			triggerRef(currentDayDuties);
			triggerRef(currentDayDutiesById);
			return response;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadDuties({
		from,
		to,
		users,
	}: {
		from: DateTime | undefined | string;
		to: DateTime | undefined | string;
		users: string[];
	}): Promise<ApiDutiesResponse> {
		try {
			const fetchedDuties: ApiDutiesResponse = await dutyApi.findDuties(from, to, users);
			const updatedDaysMap = { ...dutiesMappedByDay.value };

			for (const [userId, userDays] of Object.entries(fetchedDuties)) {
				updatedDaysMap[userId] = {
					...updatedDaysMap[userId],
					...userDays,
				};
			}
			dutiesMappedByDay.value = updatedDaysMap;

			const updatedIdMap = { ...dutiesMappedById.value };

			for (const daysMap of Object.values(fetchedDuties)) {
				for (const duty of Object.values(daysMap)) {
					if (duty && duty.id) {
						updatedIdMap[duty.id] = duty;
					}
				}
			}
			dutiesMappedById.value = updatedIdMap;

			triggerRef(dutiesMappedByDay);
			triggerRef(dutiesMappedById);
			notifyMappedByDayChanged();
			return fetchedDuties;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadAllDuties(from: any, to: any): Promise<void> {
		try {
			const response = await dutyApi.findAllDuties(from, to);
			duties.value = response || [];
			const dutiesMap = response.reduce((acc: any, user: any) => {
				acc[user.id] = user;
				return acc;
			}, {});
			duties.value = dutiesMap;
			triggerRef(duties);
		} catch (error) {
			fetchError.value = error instanceof Error ? error.message : 'Unknown error';
			throw error;
		}
	}

	async function loadDutiesByDay(day: string): Promise<void> {
		try {
			const isDay = DateTime.fromISO(day).isValid;
			if (day && isDay) {
				const response = await dutyApi.fetchAvailableDutiesByDay(day);
				currentDayDuties.value = response || [];
				const data = response?.data?.all || [];

				const updatedIdMap = data.reduce((map: Record<string, any>, duty: any) => {
					if (duty?._id) {
						map[duty._id] = duty;
					}
					return map;
				}, {});

				currentDayDutiesById.value = updatedIdMap;
				triggerRef(currentDayDuties);
				triggerRef(currentDayDutiesById);
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadDutyActivities(dutyId: string): Promise<DutyActivity[]> {
		try {
			return await dutyApi.loadDutyActivities(dutyId);
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function getUnallocatedDuties({ days }: { days: string[] }) {
		try {
			const dutiesData = await dutyApi.loadUnallocatedDuties(days);

			if (Array.isArray(dutiesData)) {
				const state = unallocatedDutiesMappedByDay.value;
				const flatState = unallocatedDutiesById.value;
				for (const d of dutiesData) {
					const dayKey: string | undefined = Object.keys(dayStore.daysRef).find((id) => id === d.day);
					if (!dayKey) continue;

					if (!state[dayKey]) state[dayKey] = {} as any;
					if (!flatState[d.id]) flatState[d.id] = {} as any;

					state[dayKey][d.id] = d;
					flatState[d.id] = d;
				}
				const merged = { ...unallocatedDutiesById.value, ...duties.value };
				duties.value = merged as any;
				triggerRef(unallocatedDutiesMappedByDay);
				triggerRef(unallocatedDutiesById);
			}

			return dutiesData;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function unassignDuty(duty: any) {
		try {
			const res = await dutyApi.updateDuty(duty.id, {
				user: null,
				fixed: false,
				fixedBy: null,
				changeComment: duty.changeComment,
			});
			alertStore.showAlert(t('duty_unassign_success'), 'success');
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function addToExchange(dutyId: string) {
		try {
			const res = await exchangeApi.addToExchange({
				duty: dutyId,
				sender: null,
				preferences: ['noDuty'],
				type: 'exchange',
			});

			alertStore.showAlert(t('duty_shown_in_duty_exchange'), 'success');
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function deleteDuty({ dutyId, changeComment }: DeleteDutyPayload) {
		try {
			const res = await dutyApi.deleteDuty({ dutyId, changeComment });
			alertStore.showAlert(t('duty_delete_success'), 'success');
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}
	async function splitDuty({ dutyId, splitBreak, changeComment }: { dutyId: string; splitBreak: DutyBreak; changeComment: string }): Promise<ApiDuty> {
		try {
			const result = await dutyApi.splitDuty(dutyId, splitBreak, changeComment);
			return result;
		} catch (error) {
			fetchError.value = error instanceof Error ? error.message : 'Unknown error';
			throw error;
		}
	}
	async function mergeDuty({ dutyId, mergeWithId, changeComment }: { dutyId: string; mergeWithId: string; changeComment: string }): Promise<ApiDuty> {
		try {
			const result = await dutyApi.mergeDuty(dutyId, mergeWithId, changeComment);
			return result;
		} catch (error) {
			fetchError.value = error instanceof Error ? error.message : 'Unknown error';
			throw error;
		}
	}

	async function loadAgreements() {
		try {
			const from = planningStore.filters.fromDate;
			const to = planningStore.filters.toDate;
			const response = await agreementApi.loadAgreements(from, to);
			const filteredAgreements: AgreementsApiResponse[] = [];
			const localAgreementsByDuty: Record<string, AgreementsApiResponse[]> = {};

			for (let i = 0; i < response.length; i++) {
				const agreement = response[i];
				if (agreement.key === 'solution.violation.acknowledge') {
					filteredAgreements.push(agreement);

					if (!localAgreementsByDuty[agreement.duty]) {
						localAgreementsByDuty[agreement.duty] = [];
					}
					localAgreementsByDuty[agreement.duty].push(agreement);
				}
			}

			agreementsByDuty.value = localAgreementsByDuty;
			agreements.value = filteredAgreements;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadDutiesWithRootID(rootID: string, dutyID: string) {
		try {
			const res = await dutyApi.findDutiesWithRootID(rootID, dutyID);
			dutiesWithSameRootID.value = res;
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function updateRootDuties(dutiesData: ApiDuty) {
		try {
			const res = await dutyApi.updateRootDuties(dutiesData);
			alertStore.showAlert(t('duty_root_update_success'), 'success');
			return res;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function setUpdateStatistics() {
		updateStatistics.value = updateStatistics.value + 1;
	}

	const dutyCellStates = ref<Record<string | number, any>>({});

	function getDutyCellState(id: string | number) {
		return dutyCellStates.value[id] || null;
	}

	function setDutyCellState(id: string | number, state: any) {
		dutyCellStates.value[id] = state;
	}

	function clearCache() {
		dutyCellStates.value = {};
	}

	function invalidateCellState(id: string | number) {
		if (id in dutyCellStates.value) {
			delete dutyCellStates.value[id];
		}
	}

	function notifyMappedByDayChanged() {
		mappedByDayVersion.value++;
		triggerRef(dutiesMappedByDay);
	}

	return {
		getDutyCellState,
		setDutyCellState,
		clearCache,
		invalidateCellState,
		mappedByDayVersion,
		notifyMappedByDayChanged,
		dutiesMappedByDay,
		dutiesMappedById,
		duties,
		dutiesById,
		fetchError,
		allDuties,
		unallocatedDutiesMappedByDay,
		agreementsByDuty,
		agreements,
		currentDayDuties,
		currentDayDutiesById,
		unallocatedDutiesById,
		updateStatistics,
		isBackgroundLoading,
		loadingProgress,
		deletedDuties,
		setUpdateStatistics,
		startBackgroundDutiesFetch,
		terminateWorker,
		loadAllDuties,
		updateRootDuties,
		dutiesWithSameRootID,
		loadDutiesWithRootID,
		loadDuties,
		loadDutyActivities,
		loadDutiesByDay,
		createDuty,
		createNewDuty,
		updateDuty,
		unassignDuty,
		fetchDutyByID,
		getUnallocatedDuties,
		addToExchange,
		deleteDuty,
		splitDuty,
		mergeDuty,
		loadAgreements,
		fetchAvailableDutiesByDay,
	};
});
